import React, { useId } from 'react';
import PropTypes from 'prop-types';
import { Form } from 'react-final-form';

import TextFieldPill from '../../../../01_atoms/FormElements/TextFieldPill';
import Button from '../../../../01_atoms/Button';
import FormattedText from '../../../../01_atoms/FormattedText';
import { composeValidators, required, validEmail } from '../../../../../utils/formFieldValidators';
import { parseSpaceless } from '../../../../../utils/formFieldParsers';
import { pushFormSubmission as gtmPushFormSubmission } from '../../../../../utils/gtm';
import unexpectedErrorMessage from '../../../../../utils/unexpectedErrorMessage';
import fetch from '../../../../../utils/fetch';
import {
  generateClassNameByBehaviorSettings,
  behaviorSettingsProps,
} from '../../../../../utils/behaviorSettings';
import { toast } from 'react-toastify';

import styles from './index.module.scss';

const BBActivismTextForm = ({
  title,
  addressBookId = 0,
  optInType = '',
  description = '',
  notice,
  placeholder,
  submitLabel,
  behaviorSettings = null,
  uuid = null,
}) => {
  const id = useId();

  const onSubmit = ({ text }, form) => {
    const payload = {
      email: text,
      address_book_id: addressBookId,
      opt_in_type: optInType,
    };

    // Generating form ID for GTM from block title and address book ID.
    const formId = `${title.replace(/\W/g, '_').toLowerCase()}_${addressBookId}`;

    return new Promise((resolve, reject) => {
      // Send an event to GTM.
      gtmPushFormSubmission(formId);

      fetch('/dotdigital-subscribe', {
        method: 'POST',
        throwNotOk: false,
        body: payload,
        headers: {
          'Content-Type': 'application/json',
        },
      })
        .then((resp) => {
          if (resp.statusCode === 200) {
            toast.success('Thanks for subscribing!', { autoClose: 7000 });
            resolve(resp);
            return resp;
          }

          if (resp.statusCode === 400 && resp.body === 'Invalid email address') {
            toast.error(
              `The ${text} email address is not allowed. Please try to use another email address.`,
              { autoClose: 7000 },
            );
          } else {
            toast.error(
              <div
                dangerouslySetInnerHTML={{
                  __html: `Sorry, something went wrong. ${unexpectedErrorMessage()}`,
                }}
              />,
            );
          }

          resolve();
          return resp;
        })
        .then((resp) => {
          if (resp.statusCode === 200) {
            form.restart();
            form.resetFieldState('text');
          }
        })
        .catch((error) => {
          toast.error(
            <div
              dangerouslySetInnerHTML={{
                __html: `Sorry, something went wrong. ${unexpectedErrorMessage()}`,
              }}
            />,
          );
          reject(error);
          return error;
        });
    });
  };

  return (
    <div
      className={`bb ${styles['bb-activism-text-form']} ${generateClassNameByBehaviorSettings(
        behaviorSettings,
      )}`}
      id={uuid}
    >
      <div className="container">
        <div className={styles['bb-activism-text-form__content']}>
          <h2 className={styles['bb-activism-text-form__title']}>{title}</h2>

          <Form
            onSubmit={onSubmit}
            subscription={{ submitting: true, valid: true }}
            render={({ handleSubmit, submitting, valid }) => (
              <form onSubmit={handleSubmit}>
                <div className={styles['bb-activism-text-form__input-group']}>
                  <div className={styles['bb-activism-text-form__input']}>
                    <TextFieldPill
                      name="text"
                      placeholder={placeholder}
                      color="pink"
                      parse={parseSpaceless}
                      validate={composeValidators(required(), validEmail())}
                      isRequired={true}
                      id={id}
                    />
                  </div>

                  <Button
                    type="white-pink"
                    isOutlined
                    isDisabled={!valid}
                    isLoading={submitting}
                    className={styles['bb-activism-text-form__submit']}
                    data-category="Email signup block"
                    data-variant="activism"
                  >
                    {submitLabel}
                  </Button>
                </div>
              </form>
            )}
          />

          {description && (
            <FormattedText
              className={styles['bb-activism-text-form__description']}
              text={description}
            />
          )}

          <p className={styles['bb-activism-text-form__notice']}>{notice}</p>
        </div>
      </div>
    </div>
  );
};

BBActivismTextForm.propTypes = {
  title: PropTypes.string.isRequired,
  addressBookId: PropTypes.number,
  optInType: PropTypes.string,
  notice: PropTypes.string.isRequired,
  description: PropTypes.string,
  placeholder: PropTypes.string.isRequired,
  submitLabel: PropTypes.string.isRequired,
  behaviorSettings: behaviorSettingsProps,
  uuid: PropTypes.string,
};

export default BBActivismTextForm;
